import Swiper from 'swiper';
import 'swiper/css';
import { Autoplay, Keyboard, Mousewheel } from 'swiper/modules';
import { getRightGutter, selectAll, debounce } from '../simplerToolkit';

const sections = selectAll('section.cards .swiper');
if (sections.length) {

    // Breakpoints tuned by total slide count across sections
    const totalSlides = sections.reduce((n, el) => n + el.querySelectorAll('.swiper-slide').length, 0);
    const baseBreakpoints = totalSlides > 3
        ? {
            650: { slidesPerView: 1.5, spaceBetween: 16 },
            720: { slidesPerView: 2.15, spaceBetween: 20 },
            1024: { slidesPerView: 3.25, spaceBetween: 20 },
            1600: { slidesPerView: 2.85, spaceBetween: 20 },
            1800: { slidesPerView: 2.55, spaceBetween: 20 },
        }
        : {
            650: { slidesPerView: 2.25, spaceBetween: 16 },
            720: { slidesPerView: 2.25, spaceBetween: 20 },
            1024: { slidesPerView: 3, spaceBetween: 20 },
        };

    const registry = [];

    sections.forEach((el) => {
        if (el.closest('section').classList.contains('carousel')) {
            const edgeRight = el.classList.contains('edge-right');
            const canAutoPlay = el.getAttribute('data-autoPlay') !== '0';
            let swiperConfig = {
                slidesPerView: 1.25,
                spaceBetween: 16,
                watchOverflow: true,
                loop: !edgeRight,
                rewind: edgeRight,
                breakpoints: baseBreakpoints,
                 modules: [Keyboard, Mousewheel],
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                mousewheel: {
                    forceToAxis: true,
                    sensitivity: 1,
                },
                ...(edgeRight ? { slidesOffsetAfter: getRightGutter(el) } : {}),
            };

            if(canAutoPlay) {
                swiperConfig.modules = [Autoplay];
                swiperConfig.autoplay = {
                    delay: 3500,
                    pauseOnMouseEnter: true,
                };
            }

            const swiper = new Swiper(el, swiperConfig);
            registry.push({ element: el, swiper, edgeRight });
        } else {
            const canAutoPlay = el.getAttribute('data-autoPlay') !== '0';
            let swiperConfig = {
                slidesPerView: 1,
                spaceBetween: 16,
                watchOverflow: true,
                loop: true,
            };

            if(canAutoPlay) {
                swiperConfig.modules = [Autoplay];
                swiperConfig.autoplay = {
                    delay: 3500,
                    pauseOnMouseEnter: true,
                };
            }

            // type: wide-card
            const swiper = new Swiper(el, canAutoPlay);
            registry.push({ element: el, swiper });
        }
    });

    // Keep edge-right gutters correct on resize
    window.addEventListener('resize', debounce(() => {
        registry.forEach(({ element, swiper, edgeRight }) => {
            if (!edgeRight) return;
            swiper.params.slidesOffsetAfter = getRightGutter(element);
            swiper.update();
        });
    }));

    // Active state helpers for wide-card navigation
    const setActiveNav = (sectionEl, slideId) => {
        const links = sectionEl.querySelectorAll('.cards-navigation a');
        if (!links.length) return;
        links.forEach((a) => a.classList.toggle('active', a.dataset.slide === slideId));
    };

    const nonDupSlides = (swiperEl) => Array
        .from(swiperEl.querySelectorAll('.swiper-slide'))
        .filter((s) => !s.classList.contains('swiper-slide-duplicate'));

    const refreshActiveFromSwiper = (swiperEl, swiper) => {
        const sectionEl = swiperEl.closest('section.cards');
        if (!sectionEl || !sectionEl.querySelector('.cards-navigation')) return;

        const slides = nonDupSlides(swiperEl);
        const idx = swiper.params.loop ? swiper.realIndex : swiper.activeIndex;
        const id = slides[idx]?.id;
        if (id) setActiveNav(sectionEl, id);
    };

    registry.forEach(({ element: swiperEl, swiper }) => {
        refreshActiveFromSwiper(swiperEl, swiper);
        swiper.on('slideChange', () => refreshActiveFromSwiper(swiperEl, swiper));
    });

    // Custom navigation for wide-card sections
    const navLinks = selectAll('section.cards .cards-navigation a');
    if (navLinks.length) {
        const resolveIndex = (swiperEl, slideId) => {
            const slides = nonDupSlides(swiperEl);
            let idx = slides.findIndex((s) => s.id === slideId);
            if (idx === -1) {
                const el = swiperEl.querySelector(`[id="${slideId}"]`);
                const raw = el?.getAttribute('data-swiper-slide-index');
                if (raw) idx = parseInt(raw, 10);
            }
            return Number.isFinite(idx) ? idx : -1;
        };

        navLinks.forEach((link) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const slideId = link.dataset.slide;
                if (!slideId) return;

                const sectionEl = link.closest('section.cards');
                const swiperEl = sectionEl?.querySelector('.swiper');
                if (!sectionEl || !swiperEl) return;

                const reg = registry.find((r) => r.element === swiperEl);
                if (!reg) return;

                setActiveNav(sectionEl, slideId);

                const targetIndex = resolveIndex(swiperEl, slideId);
                if (targetIndex > -1) {
                    reg.swiper.params.loop
                        ? reg.swiper.slideToLoop(targetIndex)
                        : reg.swiper.slideTo(targetIndex);
                }
            });
        });
    }

}
